import { Component, Input, OnInit, ViewChild } from '@angular/core';
import { IndicationDefinition, IndicationGroup } from '@app/models/indication';
import { InputOption } from '@app/models/input-option';
import { ControlTypeEnum, SelectionsModel } from '@app/models/selections-model';
import { SharedService } from '@app/services/shared.service';
import { RadioInputGroupComponent } from '@app/shared/inputs/radio-input-group/radio-input-group.component';
import { VennHealthHelpers } from '@app/shared/vennHealthHelpers';
import { asapScheduler, first, skip } from 'rxjs';

@Component({
  selector: 'app-hepatitis',
  templateUrl: './hepatitis.component.html',
  styleUrls: ['./hepatitis.component.css']
})
export class HepatitisComponent implements OnInit {

  @ViewChild('childScreeningTestComponent') childScreeningTestComponent: RadioInputGroupComponent;
  @ViewChild('childPopulationsComponent') childPopulationsComponent: RadioInputGroupComponent;

  @Input() indication: IndicationDefinition;

  // copy of indication where we add the screening test inputOptions to pass to the 1st radio button child component
  screeningTestIndication: IndicationDefinition;

  // copy of indication where we add the populations inputOptions to pass to the 1st radio button child component
  populationsIndication: IndicationDefinition;
  
  allIndications: IndicationGroup[] = [];

  childScreeningTestSelectionModel: SelectionsModel;
  childPopulationsSelectionModel: SelectionsModel;

  hepatitisA = 'Hepatitis A';
  hepatitisB = 'Hepatitis B';
  hepatitisC = 'Hepatitis C';
  hepatitisD = 'Hepatitis D';

  hepatitisVirusesList = [this.hepatitisA, this.hepatitisB, this.hepatitisC, this.hepatitisD];
  selectedHepatitisVirus = this.hepatitisVirusesList[0];

  screeningTestInputOptions: InputOption[] = [
    {name: 'AB', displayName: 'Antibody', order: 1, defaultValue: 'AB'},
    {name: 'SA', displayName: 'Surface Antigen', order: 2, defaultValue: 'AB'}
  ];

  populationsInputOptions: any[] = [
    {name: 'DO', displayName: 'Diagnosed Only', order: 1, defaultValue: 'DU'},
    {name: 'MC', displayName: 'Defined by lab values (All Patients)', order: 2, defaultValue: 'DU', checkbox: [
        { name: 'MC', displayName: 'Defined by lab values only', order: 1},
        { name: 'MC_AM', displayName: 'Add patients taking medication', order: 2 }, 
        { name: 'MC_LM', displayName: 'Limit to patients taking medication', order: 3}
      ]
    },
    {name: 'UO', displayName: 'Undiagnosed Only (Defined by lab values)', order: 3, defaultValue: 'DU'},
    {name: 'DU', displayName: 'Diagnosed + Undiagnosed (Defined by lab values)', order: 4, defaultValue: 'DU'},
    {name: 'TO', displayName: 'Treated Patients Only', order: 5, defaultValue: 'DU'}
  ];

  constructor(
    private sharedService: SharedService
  ) { }

  ngOnInit() {
    this.assignChildIndicationDetails();

    // only called first time to setup form and update selected filters via shared service
    this.sharedService.allIndications.pipe(first()).subscribe(data => {
      if (data) {
        this.allIndications = data;
        this.setIndicationSelections();
      }
    });

    // called everytime after the first time
    this.sharedService.allIndications.pipe(skip(1)).subscribe(data => {
      if (data) {
        this.allIndications = data;
      }
    });

    this.sharedService.activeSavedFilters.subscribe(data => {
      if (data) {
        const loadedIndication = VennHealthHelpers.getCurrentIndicationFromIndicationGroupJson(this.indication, this.allIndications);
        this.indication.selections = loadedIndication.selections;
        this.indication.selected = loadedIndication.selected;

        this.assignChildIndicationDetails();
        this.setIndicationSelections();
      }
    });
  }

  assignChildIndicationDetails() {
    this.populationsIndication = Object.assign({}, this.indication);
    this.populationsIndication.selections = this.populationsIndication.selections.filter(i => i.controlName === 'populations');
    this.populationsIndication.inputOptions = this.populationsInputOptions;
    if (this.populationsIndication.selections && this.populationsIndication.selections.length > 0) {
      this.childPopulationsSelectionModel = this.populationsIndication.selections[0];
    }

    this.screeningTestIndication = Object.assign({}, this.indication);
    this.screeningTestIndication.selections = this.screeningTestIndication.selections.filter(i => i.controlName === 'screeningtest');
    this.screeningTestIndication.inputOptions = this.screeningTestInputOptions;
    if (this.screeningTestIndication.selections && this.screeningTestIndication.selections.length > 0) {
      this.childScreeningTestSelectionModel = this.screeningTestIndication.selections[0];
    }
  }

  setIndicationSelections() {
    if (this.indication.selected && this.indication.selections.length > 0) {
      // dropdown
      this.selectedHepatitisVirus = this.indication.selections.find(i => i.controlName === 'virus').selectionValue

      // child populations radio button group
      if (this.indication.selections.some(i => i.controlName === 'populations')) {
        if (this.childPopulationsComponent) {
          this.childPopulationsComponent.overrideInputValue(this.indication.selections.find(i => i.controlName === 'populations').selectionValue);
        }
      }

      // child screeningtest radio button group
      if (this.indication.selections.some(i => i.controlName === 'screeningtest')) {
        if (this.childScreeningTestComponent) {
          this.childScreeningTestComponent.overrideInputValue(this.indication.selections.find(i => i.controlName === 'screeningtest').selectionValue);
        }
      }

      this.updateSelections();
    } else {
      this.setDefaults();
    }
  }

  childSharedScreeningTestControlSelectionChangedHandler(selectionModel: SelectionsModel) {
    this.childScreeningTestSelectionModel = selectionModel;
    this.updateSelections();
  }

  childSharedPopulationsControlSelectionChangedHandler(selectionModel: SelectionsModel) {
    this.childPopulationsSelectionModel = selectionModel;
    this.updateSelections();
  }

  showScreeningTest(): boolean {
    const showScreeningTest = ['MC', 'MC_AM', 'MC_LM', 'UO', 'DU'];
    if (this.selectedHepatitisVirus === this.hepatitisB && this.childPopulationsSelectionModel && 
      showScreeningTest.includes(this.childPopulationsSelectionModel.selectionValue)) {
      return true;
    }
    return false;
  }

  setDefaults() {
    asapScheduler.schedule(() => {
      this.selectedHepatitisVirus = this.hepatitisVirusesList[0];
      if (this.childPopulationsComponent) {
        this.childPopulationsComponent.overrideInputValue('DU');
      }
      if (this.childScreeningTestComponent) {
        this.childScreeningTestComponent.overrideInputValue('AB');
      }
      this.updateSelections();
    });
  }

  hepBorCSelected(): boolean {
    return this.selectedHepatitisVirus === this.hepatitisB || this.selectedHepatitisVirus === this.hepatitisC;
  }

  updateSelections(): void {
    const currentSelections: SelectionsModel[] = [];

    const criteriaSelection: SelectionsModel = {
      indicationName: this.indication.name,
      controlName: 'virus',
      controlType: ControlTypeEnum.DROPDOWN,
      controlDisplayName: "Virus",
      order: 1,
      selectionValue: this.selectedHepatitisVirus,
      selectionDisplayName: this.selectedHepatitisVirus
    };
    currentSelections.push(criteriaSelection);

    if (this.childPopulationsSelectionModel) {
      currentSelections.push(this.childPopulationsSelectionModel);
    }

    if (this.childScreeningTestSelectionModel) {
      currentSelections.push(this.childScreeningTestSelectionModel);
    }
    
    this.indication.selections = currentSelections;
    const currentIndicationGroup = VennHealthHelpers.getIndicationGroup(this.indication);
    const matchingGroupIndex = this.allIndications.findIndex(i => i.name === currentIndicationGroup.name);
    const matchingIndicationIndex = this.allIndications[matchingGroupIndex].indications.findIndex(i => i.name === this.indication.name);
    this.allIndications[matchingGroupIndex].indications[matchingIndicationIndex] = this.indication;
    this.sharedService.setAllIndications(this.allIndications);
  }
}
